import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Constants from '../utils/Constants';
import fetchMolContent from '../services/contentService';

const centerView = `
// --- Centering and Zooming (to be placed at the end of your script) ---

// 1. Reset the view (optional but recommended for consistency)
reset;

// 2. Select everything that is currently displayed (or select all if you want to ensure everything
// is included)
select displayed;

// 3. Center the view on the selection
center selected;

// 4. Set the zoom to 50%
zoom 50;

`;

function createBlob(data) {
  if (typeof data === 'string') {
    return new Blob([data], { type: 'text/plain' });
  }
  return data;
}

function MolViewer({
  molContent,
  viewType = 'mol',
  fnCb = () => {},
  fnInit = () => {},
  src = '',
  src2 = '',
  jmScripts = [],
}) {
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const [apiMolContent, setApiMolContent] = useState(null);

  const handleMessage = useCallback(
    event => {
      const eventUrl = new URL(event.origin);
      const windowUrl = new URL(window.location.host);
      if (eventUrl.hostname !== windowUrl.hostname) {
        return;
      }
      const { type, data } = event.data;
      if (type === Constants.POST.TYPE) {
        if (data === Constants.POST.COMPLETE) {
          fnCb();
        } else if (data === Constants.POST.START) {
          fnInit();
        }
      }
    },
    [fnInit, fnCb]
  );

  useEffect(() => {
    fnInit();
    window.addEventListener(Constants.EVENT.MESSAGE, handleMessage);
    return () => {
      window.removeEventListener(Constants.EVENT.MESSAGE, handleMessage);
    };
  }, [handleMessage, fnInit]);

  useEffect(() => {
    const loadMolContent = async () => {
      if (isIframeLoaded && viewType) {
        if (/^file/.test(viewType)) {
          setApiMolContent(molContent);
        } else if (/^mol/.test(viewType)) {
          try {
            const fetchedMolContent = await fetchMolContent(src, molContent);
            setApiMolContent(createBlob(fetchedMolContent));
          } catch (error) {
            console.log('error=', error);
            setApiMolContent(createBlob(molContent));
          }
        }
      }
    };
    loadMolContent();
  }, [viewType, isIframeLoaded, src, molContent]);

  useEffect(() => {
    if (!apiMolContent) return;

    const iframe = document.getElementById(Constants.IFRAME.ELEMENT_ID);
    let runScript =
      'set echo top left;font echo 18 arial italic;echo "loading file...processing...';
    runScript +=
      'this might take a while if the file contains a complex structure.";refresh;';

    const postMessage = script => {
      iframe.contentWindow.postMessage(
        { type: Constants.POST.TYPE, data: script },
        '*'
      );
    };

    if (/^file/.test(viewType)) {
      runScript += ` load async "${apiMolContent}"; `;
      runScript += jmScripts.join(' ');
      runScript += ' echo ""; ';
      runScript += centerView;
      postMessage(runScript);
    } else if (/^mol/.test(viewType)) {
      apiMolContent.text().then(text => {
        runScript += ` load DATA "mol_example"\n${text}\nend "mol_example"; `;
        runScript += jmScripts.join(' ');
        runScript += 'show data; echo ""; ';
        runScript += centerView;
        postMessage(runScript);
      });
    }
  }, [apiMolContent, jmScripts, viewType]);

  if (!/^file|^mol/.test(viewType)) {
    return <span>{Constants.MESSAGE.NOT_SUPPORTED}</span>;
  }
  if (!molContent) {
    return <span>{Constants.MESSAGE.NO_CONTENT}</span>;
  }

  return (
    <div className="structure-frame-container">
      <iframe
        id={Constants.IFRAME.ELEMENT_ID}
        src={`${src2}/jsmol/index.htm`}
        title="Molecule Viewer Web"
        className="frame-div"
        onLoad={() => setIframeLoaded(true)}
      />
    </div>
  );
}

MolViewer.propTypes = {
  molContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
    .isRequired,
  viewType: PropTypes.string,
  fnCb: PropTypes.func,
  fnInit: PropTypes.func,
  src: PropTypes.string,
  src2: PropTypes.string,
  jmScripts: PropTypes.arrayOf(PropTypes.string),
};

MolViewer.defaultProps = {
  viewType: 'mol',
  fnCb: () => {},
  fnInit: () => {},
  src: '',
  src2: '',
  jmScripts: [],
};

export default MolViewer;
